import { useEffect, useRef, useState } from 'react'
import { fetchLatestEntryScript, getLoadedEntryScript, isNewVersion } from '@/lib/versionCheck'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const FOCUS_THROTTLE_MS = 60 * 1000

const NEW_VERSION_MESSAGE = 'A new version of the dashboard is available.'
const PRELOAD_ERROR_MESSAGE = 'The dashboard was updated — please reload to continue.'

interface Props {
  /** Test seams; production uses the defaults. */
  fetchLatest?: () => Promise<string | null>
  onReload?: () => void
  disabled?: boolean
}

export default function NewVersionBanner({
  fetchLatest = fetchLatestEntryScript,
  onReload = () => window.location.reload(),
  disabled = import.meta.env.DEV,
}: Props) {
  const [message, setMessage] = useState<string | null>(null)
  const lastCheck = useRef(0)

  useEffect(() => {
    if (disabled) return
    const onPreloadError = () => setMessage(PRELOAD_ERROR_MESSAGE)
    window.addEventListener('vite:preloadError', onPreloadError)

    const current = getLoadedEntryScript()
    let timer: ReturnType<typeof setInterval> | undefined
    let onFocus: (() => void) | undefined
    let onVisible: (() => void) | undefined

    if (current) {
      const check = async () => {
        lastCheck.current = Date.now()
        const latest = await fetchLatest()
        if (isNewVersion(current, latest)) {
          setMessage(prev => prev ?? NEW_VERSION_MESSAGE)
        }
      }
      const maybeCheck = () => {
        if (Date.now() - lastCheck.current >= FOCUS_THROTTLE_MS) void check()
      }
      timer = setInterval(() => void check(), CHECK_INTERVAL_MS)
      onFocus = maybeCheck
      onVisible = () => { if (document.visibilityState === 'visible') maybeCheck() }
      window.addEventListener('focus', onFocus)
      document.addEventListener('visibilitychange', onVisible)
    }

    return () => {
      window.removeEventListener('vite:preloadError', onPreloadError)
      if (timer) clearInterval(timer)
      if (onFocus) window.removeEventListener('focus', onFocus)
      if (onVisible) document.removeEventListener('visibilitychange', onVisible)
    }
  }, [disabled, fetchLatest])

  if (!message) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[100] -translate-x-1/2 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-lg"
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onReload}
        className="rounded-md bg-primary px-3 py-1.5 font-medium text-white hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        Reload
      </button>
    </div>
  )
}
